/* Clignote : cœur partagé v3 (logique pure, aucune interface). Chargé juste après engine.js.
   Testé hors navigateur par _tests/coeur-test.js (Node) et dans l'appli par _tests/selftest-v3.js.
   Contenu : tempo au doigt, sensibilité + seuil automatique, détection des temps forts, flashs synchronisés,
   piste de danse, répartition des lettres, emoji en pixels, polices (actuelle, grasse, fine, ma police),
   animation image par image, enchaînements, SOS à double confirmation. */
(function (global) {
  "use strict";

  /* ================= 4. TEMPO AU DOIGT =================
     Moyenne des 4 à 8 derniers taps. Une pause de plus de 2 s recommence le comptage. */
  function creerTapTempo(opt) {
    const o = Object.assign({ min: 4, max: 8, pause: 2000 }, opt || {});
    const s = { taps: [], bpm: null, phase: null };
    s.tap = t => {
      if (s.taps.length && t - s.taps[s.taps.length - 1] > o.pause) s.taps = [];
      s.taps.push(t); if (s.taps.length > o.max) s.taps.shift();
      s.phase = t;
      if (s.taps.length < o.min) return null;
      const moy = (s.taps[s.taps.length - 1] - s.taps[0]) / (s.taps.length - 1);
      s.bpm = Math.max(30, Math.min(300, Math.round(60000 / moy)));
      return s.bpm;
    };
    s.reste = () => Math.max(0, o.min - s.taps.length);
    s.reset = () => { s.taps = []; s.bpm = null; s.phase = null; };
    return s;
  }

  /* ================= 1. SENSIBILITÉ (GAIN) ET SEUIL AUTOMATIQUE =================
     Le plancher suit le bruit ambiant : il descend vite (silence) et monte lentement (la musique ne le rattrape pas).
     niveau = (brut - plancher - marge) x gain, borné à 0..1. */
  const PRESETS = {
    boite: { nom: "Boîte de nuit", sensib: 3, marge: 0.18 },
    salon: { nom: "Salon", sensib: 9, marge: 0.03 },
  };
  const gainDe = sensib => Math.max(1, Math.min(10, sensib)) / 3;
  function creerNiveaux(opt) {
    const s = Object.assign({ sensib: 6, marge: 0.08, planchers: [] }, opt || {});
    s.traiter = bruts => bruts.map((v, i) => {
      let p = s.planchers[i]; if (p == null) p = v;
      p += (v - p) * (v < p ? 0.3 : 0.004);
      s.planchers[i] = p;
      return Math.max(0, Math.min(1, (v - p - s.marge) * gainDe(s.sensib) * 2.2));
    });
    s.preset = nom => { const p = PRESETS[nom]; if (p) { s.sensib = p.sensib; s.marge = p.marge; } return p; };
    return s;
  }

  /* ================= 4. TEMPS FORTS AU MICRO =================
     Énergie des basses comparée à un seuil adaptatif (moyenne + k écarts-types sur ~1 s), 280 ms minimum entre deux temps. */
  function creerDetecteur(opt) {
    const o = Object.assign({ fenetre: 60, k: 1.4, ecartMin: 280, plancherAbs: 0.04, rapportMin: 1.25 }, opt || {});
    const s = { hist: [], temps: [], dernier: -1e9 };
    s.pousser = (e, t) => {
      const h = s.hist, n = h.length;
      let fort = false;
      if (n >= 10) {
        const m = h.reduce((a, b) => a + b, 0) / n;
        const sd = Math.sqrt(h.reduce((a, b) => a + (b - m) * (b - m), 0) / n);
        const seuil = Math.max(m + o.k * sd, m * o.rapportMin, o.plancherAbs);
        fort = e > seuil && t - s.dernier >= o.ecartMin;
        s.seuil = seuil;
      }
      h.push(e); if (h.length > o.fenetre) h.shift();
      if (fort) { s.dernier = t; s.temps.push(t); if (s.temps.length > 17) s.temps.shift(); }
      return fort;
    };
    /* BPM = médiane des écarts entre les derniers temps forts, ramenée entre 70 et 180 */
    s.bpm = () => {
      if (s.temps.length < 4) return null;
      const ec = []; for (let i = 1; i < s.temps.length; i++) ec.push(s.temps[i] - s.temps[i - 1]);
      ec.sort((a, b) => a - b);
      let b = 60000 / ec[ec.length >> 1];
      while (b < 70) b *= 2; while (b > 180) b /= 2;
      return Math.round(b);
    };
    s.reset = () => { s.hist = []; s.temps = []; s.dernier = -1e9; };
    return s;
  }
  /* énergie des basses : moyenne des premières cases de l'analyseur (0..255) ramenée à 0..1 */
  const energieBasses = (buf, n) => { n = n || 3; let a = 0; for (let i = 0; i < n; i++) a += buf[i] || 0; return a / n / 255; };

  /* ================= 12. FLASHS SYNCHRONISÉS SUR PLUSIEURS PAIRES =================
     Une paire lente reçoit l'ordre plus tôt : envoi = temps fort - décalage de la paire. Renvoie le prochain envoi >= maintenant. */
  function prochainEnvoi(phase, periode, decalage, maintenant) {
    if (!(periode > 0)) return null;
    const cible = maintenant + (decalage || 0);
    const k = Math.ceil((cible - phase) / periode - 1e-9);
    return phase + k * periode - (decalage || 0);
  }
  const dureeFlash = periode => Math.max(150, Math.min(220, periode / 2)); // jamais plus d'un envoi toutes les 150 ms

  /* ================= 20. PISTE DE DANSE ================= */
  function animSuivante(ids, actuelle, alea) {
    const r = alea || Math.random;
    const l = ids.filter(x => x !== actuelle);
    if (!l.length) return ids[0] || actuelle || null;
    return l[Math.floor(r() * l.length) % l.length];
  }

  /* ================= 13. PHOTO DE GROUPE LETTRE PAR LETTRE =================
     Une lettre (ou un morceau de 1 à 5 lettres) par paire, dans l'ordre ; morceaux équilibrés, les plus longs d'abord. */
  function repartirLettres(mot, nbPaires) {
    const n = Math.max(0, nbPaires | 0);
    const l = [...String(mot || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/\s+/g, "")];
    if (!l.length) return { ok: false, raison: "vide", morceaux: new Array(n).fill("") };
    if (!n) return { ok: false, raison: "paires", morceaux: [] };
    if (l.length > 5 * n) return { ok: false, raison: "trop", manque: Math.ceil(l.length / 5) - n, morceaux: new Array(n).fill("") };
    const k = Math.min(n, l.length), base = Math.floor(l.length / k), reste = l.length % k, morceaux = [];
    let i = 0;
    for (let p = 0; p < n; p++) { const t = p < k ? base + (p < reste ? 1 : 0) : 0; morceaux.push(l.slice(i, i + t).join("")); i += t; }
    return { ok: true, morceaux };
  }
  /* réordonner : déplace l'élément i à la place j (glisser) */
  function deplacer(liste, i, j) { const l = liste.slice(); if (i < 0 || i >= l.length) return l; j = Math.max(0, Math.min(l.length - 1, j)); l.splice(j, 0, l.splice(i, 1)[0]); return l; }

  /* ================= 6. EMOJI EN PIXELS =================
     40 emoji courants, dessinés à la main en 9 lignes (9 ou 12 colonnes), monochromes. Pas de conversion automatique. */
  const EMOJI = [
    ["❤", "cœur", ".##...##.|####.####|#########|#########|#########|.#######.|..#####..|...###...|....#...."],
    ["🔥", "feu", "....#....|...##....|...###.#.|..####.#.|.#######.|.###.###.|##.#.#.##|##.....##|.#######."],
    ["😂", "mort de rire", "..#####..|.#.....#.|#.#...#.#|##.#.#.##|#.......#|#.#####.#|#..###..#|.#.....#.|..#####.."],
    ["😘", "bisou", "..#####..|.#.....#.|#.#..##.#|#.......#|#....#..#|#...#.#.#|#....#..#|.#.....#.|..#####.."],
    ["⭐", "étoile", "....#....|....#....|...###...|#########|.#######.|..#####..|..##.##..|.##...##.|.#.....#."],
    ["🎉", "fête", ".......#.|.#..#....|.....#.#.|...##..#.|..###.#..|.####....|.###..#.#|####.....|##......."],
    ["🍺", "bière", ".#####...|#######..|#.....###|#.#.#.#.#|#.#.#.#.#|#.#.#.###|#.#.#.#..|#.....#..|.#####..."],
    ["☀", "soleil", "#...#...#|.#.....#.|...###...|..#####..|#.#####.#|..#####..|...###...|.#.....#.|#...#...#"],
    ["😎", "lunettes de soleil", "..#####..|.#.....#.|#########|####.####|###...###|#.......#|#.#...#.#|.#.###.#.|..#####.."],
    ["😍", "yeux cœurs", "..#####..|.#.....#.|##.#.#.##|####.####|#.#...#.#|#.......#|#.#####.#|.#.###.#.|..#####.."],
    ["👍", "pouce", "....##...|...##....|..###....|#.######.|#.#######|#.######.|#.#######|#.######.|#..####.."],
    ["😉", "clin d'œil", "..#####..|.#.....#.|#.......#|#.#..##.#|#.......#|#.#...#.#|#..###..#|.#.....#.|..#####.."],
    ["😊", "sourire", "..#####..|.#.....#.|#.......#|#.#...#.#|#.......#|#.#...#.#|#..###..#|.#.....#.|..#####.."],
    ["😢", "larme", "..#####..|.#.....#.|#.......#|#.#...#.#|#.#.....#|##.###..#|#.#...#.#|.#.....#.|..#####.."],
    ["😡", "colère", "..#####..|.##...##.|#..#.#..#|#.......#|#.#...#.#|#.......#|#..###..#|.##...##.|..#####.."],
    ["💀", "crâne", "..#####..|.#######.|#########|##..#..##|##..#..##|#########|.###.###.|..#####..|..#.#.#.."],
    ["👻", "fantôme", "..#####..|.#######.|##..#..##|##..#..##|#########|#########|#########|#########|#.#.#.#.#"],
    ["🎂", "gâteau", "..#.#.#..|..#.#.#..|.#######.|.#.#.#.#.|#########|#.......#|#########|#.......#|#########"],
    ["🍾", "champagne", "....#....|...###...|....#....|...###...|..#####..|..#...#..|..#####..|..#####..|..#####.."],
    ["🥂", "santé", "#...#..#...#|#...#..#...#|#####..#####|.###....###.|..#......#..|..#......#..|..#......#..|..#......#..|.###....###."],
    ["🍸", "cocktail", "#########|.#.....#.|..#...#..|...#.#...|....#....|....#....|....#....|....#....|..#####.."],
    ["🎵", "musique", "...######|...#....#|...#....#|...#....#|...#....#|.###..###|####.####|####.####|.##...##."],
    ["💋", "bouche", ".........|.........|.##...##.|####.####|#########|.#######.|..#####..|.........|........."],
    ["💯", "cent", ".#..###.###.|##..#.#.#.#.|.#..#.#.#.#.|.#..#.#.#.#.|.#..#.#.#.#.|###.###.###.|............|############|.##########."],
    ["⚡", "éclair", ".....###.|....###..|...###...|..######.|.....##..|....##...|...##....|..##.....|.#......."],
    ["🌙", "lune", "..####...|.###.....|###......|###......|###......|###......|###......|.###.....|..####..."],
    ["☁", "nuage", ".........|.........|...###...|..#####..|.########|#########|#########|.#######.|........."],
    ["❄", "flocon", "....#....|.#..#..#.|..#.#.#..|...###...|#########|...###...|..#.#.#..|.#..#..#.|....#...."],
    ["🍕", "pizza", "#########|#########|.#.....#.|.#.#.#.#.|..#...#..|..#.#.#..|...#.#...|....#....|........."],
    ["🎁", "cadeau", "..##.##..|...###...|#########|#...#...#|#########|#...#...#|#...#...#|#...#...#|#########"],
    ["👑", "couronne", ".........|#...#...#|##.###.##|#########|#########|#.#.#.#.#|#########|.........|........."],
    ["💎", "diamant", ".........|..#####..|.#.#.#.#.|#########|.#.....#.|..#...#..|...#.#...|....#....|........."],
    ["🐱", "chat", "#.......#|##.....##|#.#####.#|#.......#|#.#...#.#|#...#...#|##.###.##|#.......#|.#######."],
    ["🍀", "trèfle", ".##...##.|####.####|####.####|.#######.|...###...|.#######.|####.####|####.####|.##.#.##."],
    ["👀", "yeux", "............|.####..####.|#....##....#|#.##.##.##.#|#.##.##.##.#|#....##....#|#....##....#|.####..####.|............"],
    ["💤", "dodo", ".....####|.......#.|......#..|.....####|####.....|...#.....|..#......|.#.......|####....."],
    ["✨", "paillettes", ".......#.|...#..###|...#...#.|.##.##...|...#.....|...#..#..|.....###.|......#..|........."],
    ["💩", "caca", "....##...|...###...|..#####..|.##.#.##.|.#######.|##.....##|#########|#########|.#######."],
    ["🎃", "citrouille", "....##...|.###.###.|#########|##.###.##|#########|#.#.#.#.#|##.....##|#########|.#######."],
    ["❌", "croix", "##.....##|###...###|.###.###.|..#####..|...###...|..#####..|.###.###.|###...###|##.....##"],
  ].map(([e, nom, m]) => ({ e, nom, motif: m.split("|") }));
  const EMOJI_PAR = {}; EMOJI.forEach(x => EMOJI_PAR[x.e] = x);
  const ALIAS = { "♥": null, "💖": "❤", "💕": "❤", "♡": "❤", "🌟": "⭐", "🥳": "🎉", "🍻": "🍺", "🌞": "☀", "🤣": "😂", "😭": "😢", "🍷": "🍸", "🎶": "🎵", "✌": null };
  /* forme courante : sans sélecteur de variante (FE0F), sans couleur de peau, premier élément d'une séquence ZWJ */
  function normaliserEmoji(tok) {
    let t = String(tok).replace(/[︎️]/g, "").replace(/[\u{1F3FB}-\u{1F3FF}]/gu, "").split("‍")[0];
    if (ALIAS[t]) t = ALIAS[t];
    return t;
  }
  const estEmoji = tok => !!EMOJI_PAR[normaliserEmoji(tok)];
  /* colonnes 9 bits d'un motif (bit y = ligne y de l'écran, 0 en haut) */
  function colsMotif(lignes) {
    const w = Math.max(...lignes.map(r => r.length)), cols = new Array(w).fill(0);
    lignes.forEach((r, y) => [...r].forEach((c, x) => { if (c === "#") cols[x] |= 1 << y; }));
    return cols;
  }
  const colsEmoji = tok => { const x = EMOJI_PAR[normaliserEmoji(tok)]; return x ? colsMotif(x.motif) : null; };
  /* découpe un texte en caractères, en gardant chaque emoji (même composé) d'un seul tenant */
  function decouper(str) {
    const out = [], ch = [...String(str || "")];
    for (let i = 0; i < ch.length; i++) {
      let c = ch[i];
      while (i + 1 < ch.length && /[︎️\u{1F3FB}-\u{1F3FF}]/u.test(ch[i + 1])) c += ch[++i];
      while (i + 2 < ch.length && ch[i + 1] === "‍") { c += ch[i + 1] + ch[i + 2]; i += 2; }
      if (/^[︎️‍]$/.test(c)) continue;
      out.push(estEmoji(c) ? normaliserEmoji(c) : c);
    }
    return out;
  }

  /* ================= 15. POLICES =================
     actuelle (police d'origine), grasse (trait doublé vers le bas, même largeur : 5 lettres tiennent toujours),
     fine (lettres étroites de 3 colonnes), perso (« Ma police » : 26 minuscules + 10 chiffres dessinés sur 9 lignes).
     Convention des colonnes : police d'origine = bits relatifs à la ligne 1 (y0 = 1) ; emoji et « Ma police » = 9 bits, ligne 0 en haut. */
  const CARACTERES_PERSO = "abcdefghijklmnopqrstuvwxyz0123456789".split("");
  const police = { nom: "actuelle", perso: {} };
  function definirPolice(nom, perso) {
    police.nom = ["actuelle", "grasse", "fine", "perso"].includes(nom) ? nom : "actuelle";
    if (perso) police.perso = perso;
    return police.nom;
  }
  /* glyphe perso : 9 lignes de 1 à 5 colonnes, colonnes vides de droite retirées */
  function glyphePerso(lignes) {
    if (!Array.isArray(lignes) || lignes.length !== 9) return null;
    const cols = colsMotif(lignes);
    while (cols.length > 1 && !cols[cols.length - 1]) cols.pop();
    return cols.some(Boolean) ? cols.slice(0, 5) : null;
  }
  /* glyphe d'un caractère (déjà nettoyé) dans la police choisie : { cols, neuf } */
  function glyphe(ch, F, FS) {
    F = F || FONT; FS = FS || FONT_S;
    const e = colsEmoji(ch); if (e) return { cols: e, neuf: true };
    if (police.nom === "perso" && police.perso[ch]) { const g = glyphePerso(police.perso[ch]); if (g) return { cols: g, neuf: true }; }
    const base = F[ch] || F["?"];
    if (police.nom === "grasse") return { cols: base.map(c => (c | (c << 1)) & 0x7f), neuf: false };
    if (police.nom === "fine" && FS[ch]) return { cols: FS[ch].map(c => c << 1), neuf: false };
    return { cols: base, neuf: false };
  }
  /* modèle de départ de l'éditeur « Ma police » : le glyphe actuel posé sur la grille 9 lignes x 5 colonnes */
  function lignesDepuisPolice(ch, F) {
    F = F || FONT; const base = (F[ch] || F["?"]).slice(0, 5), out = [];
    for (let y = 0; y < 9; y++) { let r = ""; for (let x = 0; x < 5; x++) r += y >= 1 && ((base[x] || 0) >> (y - 1)) & 1 ? "#" : "."; out.push(r); }
    return out;
  }

  /* ================= 9. ANIMATION IMAGE PAR IMAGE ================= */
  const FILM_MAX = 32; // limite à confirmer sur la vraie paire
  const filmCreer = (px, duree) => ({ images: [px.slice()], durees: [duree || 300] });
  function filmAjouter(f, apres, px) { if (f.images.length >= FILM_MAX) return false; f.images.splice(apres + 1, 0, px.slice()); f.durees.splice(apres + 1, 0, f.durees[apres] || 300); return true; }
  const filmDupliquer = (f, i) => filmAjouter(f, i, f.images[i]);
  function filmSupprimer(f, i) {
    if (f.images.length <= 1) return null;
    const retire = { image: f.images.splice(i, 1)[0], duree: f.durees.splice(i, 1)[0], index: i };
    return retire;
  }
  function filmRemettre(f, r) { f.images.splice(r.index, 0, r.image); f.durees.splice(r.index, 0, r.duree); }
  function filmDeplacer(f, i, j) { f.images = deplacer(f.images, i, j); f.durees = deplacer(f.durees, i, j); return Math.max(0, Math.min(f.images.length - 1, j)); }
  /* index de l'image affichée au temps t (lecture en boucle) */
  function imageA(durees, t) {
    const tot = durees.reduce((a, b) => a + Math.max(50, b), 0); if (!tot) return 0;
    let r = ((t % tot) + tot) % tot;
    for (let i = 0; i < durees.length; i++) { const d = Math.max(50, durees[i]); if (r < d) return i; r -= d; }
    return durees.length - 1;
  }

  /* ================= 19. ENCHAÎNEMENTS =================
     éléments : { type: "texte" | "anim" | "dessin" | "film", valeur, duree (s) } ; lecture en boucle ou une fois. */
  function etapeEnchainement(elements, ms, boucle) {
    const d = (elements || []).map(e => Math.max(0.5, +e.duree || 3) * 1000), tot = d.reduce((a, b) => a + b, 0);
    if (!tot) return null;
    if (!boucle && ms >= tot) return { index: -1, fini: true, total: tot };
    let r = boucle ? ms % tot : Math.max(0, ms);
    for (let i = 0; i < d.length; i++) { if (r < d[i]) return { index: i, fini: false, reste: d[i] - r, total: tot }; r -= d[i]; }
    return { index: d.length - 1, fini: false, reste: 0, total: tot };
  }
  const dureeEnchainement = elements => (elements || []).reduce((a, e) => a + Math.max(0.5, +e.duree || 3), 0);

  /* ================= 7. SOS À DOUBLE CONFIRMATION =================
     repos -> demander() -> confirmer1 -> confirmer() -> confirmer2 -> confirmer() -> actif. Chaque confirmation expire après 6 s. */
  function creerSOS(horloge, delai) {
    const now = horloge || (() => Date.now()), D = delai || 6000;
    const s = { etat: "repos", depuis: 0 };
    const expire = () => { if ((s.etat === "confirmer1" || s.etat === "confirmer2") && now() - s.depuis > D) s.etat = "repos"; };
    s.demander = () => { expire(); if (s.etat === "repos") { s.etat = "confirmer1"; s.depuis = now(); } return s.etat; };
    s.confirmer = () => {
      expire();
      if (s.etat === "confirmer1") { s.etat = "confirmer2"; s.depuis = now(); }
      else if (s.etat === "confirmer2") { s.etat = "actif"; s.depuis = now(); }
      return s.etat;
    };
    s.annuler = () => { s.etat = "repos"; return s.etat; };
    s.lire = () => { expire(); return s.etat; };
    return s;
  }
  const lienPosition = (lat, lng) => "https://maps.google.com/?q=" + (+lat).toFixed(5) + "," + (+lng).toFixed(5);

  const Coeur = {
    creerTapTempo, PRESETS, creerNiveaux, gainDe, creerDetecteur, energieBasses, prochainEnvoi, dureeFlash, animSuivante,
    repartirLettres, deplacer, EMOJI, normaliserEmoji, estEmoji, colsEmoji, colsMotif, decouper,
    police, definirPolice, glyphe, glyphePerso, lignesDepuisPolice, CARACTERES_PERSO,
    FILM_MAX, filmCreer, filmAjouter, filmDupliquer, filmSupprimer, filmRemettre, filmDeplacer, imageA,
    etapeEnchainement, dureeEnchainement, creerSOS, lienPosition,
  };
  global.Coeur = Coeur;

  /* ---------- branchement sur le moteur (engine.js) : tout le rendu texte passe par la police choisie et les emoji ----------
     textCols garde la convention d'origine (y0 = 1) pour les animations et le calcul de largeur ;
     pickFont renvoie des colonnes 9 lignes (y0 = 0, neuf = vrai) dès qu'un emoji ou un glyphe de « Ma police » est présent. */
  if (typeof global.textCols === "function" && typeof FONT !== "undefined") {
    const pickFont0 = global.pickFont;
    global.textCols = function (str) {
      const out = [];
      for (const ch of decouper(clean(str))) { const g = glyphe(ch); out.push(...(g.neuf ? g.cols.map(c => c >> 1) : g.cols), 0); }
      out.pop(); return out;
    };
    global.colsNeuf = function (str) {
      const out = [];
      for (const ch of decouper(clean(str))) { const g = glyphe(ch); out.push(...(g.neuf ? g.cols : g.cols.map(c => c << 1)), 0); }
      out.pop(); return out;
    };
    global.pickFont = function (str, size) {
      const neuf = decouper(clean(str)).some(ch => glyphe(ch).neuf);
      if (neuf) return { cols: global.colsNeuf(str), y0: 0, small: false, neuf: true };
      return pickFont0(str, size);
    };
  }
  if (typeof module !== "undefined" && module.exports) module.exports = Coeur;
})(typeof window !== "undefined" ? window : globalThis);
